import React, { useEffect, useMemo, useRef, useState } from 'react'

import { DownOutlined, SearchOutlined } from '@ant-design/icons'
import { RefSelectProps, Select, Spin, Typography } from 'antd'
import { Company } from 'types/company'

export type HeaderCompanySelectorProps = {
  companyList: Company[]
  currentCompanyId?: string
  currentCompanyName?: string
  includeProspects?: boolean
  includeTestCompanies?: boolean
  includeInactive?: boolean
  loading?: boolean
  style?: React.CSSProperties
  /**
   * Render as a regular bordered dropdown (border + caret) instead of the
   * top bar's borderless title look. Used by the ABP dashboard page picker.
   */
  isOutlined?: boolean
  onChange?: (companyId: string) => void
}

export const HeaderCompanySelector = ({
  companyList = [],
  currentCompanyId,
  currentCompanyName,
  includeProspects = true,
  includeTestCompanies = true,
  includeInactive = true,
  loading = false,
  style,
  isOutlined = false,
  onChange,
}: HeaderCompanySelectorProps) => {
  const list = useMemo(
    () =>
      companyList
        .filter(({ isProspect, isActive, isTestClient }) => {
          if (
            (!includeProspects && isProspect) ||
            (!includeTestCompanies && isTestClient) ||
            (!includeInactive && !isActive)
          ) {
            return false
          }
          return true
        })
        .sort((a, b) => {
          const aName = (a.companyName || '').toLowerCase()
          const bName = (b.companyName || '').toLowerCase()
          if (aName > bName) return 1
          if (aName < bName) return -1
          return 0
        }),
    [companyList, includeProspects, includeTestCompanies, includeInactive]
  )

  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef<RefSelectProps>(null)

  const firstCompanyId = list[0]?.id

  useEffect(() => {
    // in case where `userSessionActions.setCurrentCompany` call was faulty (like in bug/SAFE-321)
    // then set user selected company to the first available company
    if (!currentCompanyId && firstCompanyId) {
      onChange?.(firstCompanyId)
    }
  }, [currentCompanyId, firstCompanyId, onChange])

  const options = useMemo(() => {
    const base = list.map((com) => ({
      label: com.companyName,
      value: com.id,
    }))
    const isCurrentInList =
      !!currentCompanyId && list.some((c) => c.id === currentCompanyId)
    if (currentCompanyId && !isCurrentInList && currentCompanyName) {
      return [{ label: currentCompanyName, value: currentCompanyId }, ...base]
    }
    return base
  }, [list, currentCompanyId, currentCompanyName])

  if (loading && list.length === 0) {
    return <Spin size="small" />
  }

  if (options.length === 0) return null

  if (options.length === 1) {
    return (
      <Typography.Text style={{ fontSize: 20, color: '#3A4951' }}>
        {options[0].label}
      </Typography.Text>
    )
  }

  return (
    <Select
      ref={selectRef}
      showSearch
      variant={isOutlined ? 'outlined' : 'borderless'}
      size={isOutlined ? 'middle' : 'large'}
      suffixIcon={
        isOpen ? <SearchOutlined /> : isOutlined ? <DownOutlined /> : null
      }
      placeholder="Select a company"
      value={currentCompanyId}
      onChange={(id: string) => {
        onChange?.(id)
        selectRef.current?.blur()
      }}
      onDropdownVisibleChange={setIsOpen}
      options={options}
      filterOption={(input, option) =>
        String(option?.label ?? '')
          .toLowerCase()
          .includes(input.toLowerCase())
      }
      style={{ width: 480, ...style }}
    />
  )
}
